/**
 * RESPONSIBILITY: draw the hero's scenes - the one leaving, the one showing, the one waiting - as stacked layers.
 *
 * DOM ORDER IS AGE ORDER, oldest at the bottom. The new scene dissolves in ON TOP of the old,
 * which stays fully opaque beneath until the new one has covered it, so the band never dips
 * toward its background colour half way through a change. The one waiting is drawn hidden, so
 * its photographs are already loaded when its turn comes.
 *
 * A MOVEMENT NEEDS A BEFORE. Each layer is mounted in its starting pose and only later given
 * `is-moving`, which is what the stylesheet animates towards; the scene already showing when
 * the page arrives has no earlier state, so it waits one beat after mounting.
 */
import { useEffect, useState } from 'react';
import { Picture } from '../commons/Picture';
import { autoMotion, type Slide } from './heroSlides';
import type { Scene } from './heroChoose';

type Phase = 'waiting' | 'showing' | 'leaving';

/** A framed photograph only zooms in: a sideways drift inside a small frame looks like it is trying to get out. */
const moveOf = (sceneId: number, place: number, framed: boolean) =>
  `motion-${framed ? 'zoom-in' : autoMotion(sceneId, place)}`;

function Layer({ scene, pool, phase }: { scene: Scene; pool: Slide[]; phase: Phase }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => setArmed(true), []);
  const opening = scene.id === 1;
  const on = phase !== 'waiting';
  const moving = on && (!opening || armed);
  const cls = [
    'hero-scene', `s-${scene.picks.length}`, scene.flip && 'flip', on && 'is-on', moving && 'is-moving',
  ];

  return (
    <div className={cls.filter(Boolean).join(' ')} aria-hidden={phase !== 'showing' || undefined}>
      {scene.picks.map((i, k) => {
        const s = pool[i % pool.length];
        return (
          <div className={`hero-tile t-${k}`} key={`${k}-${s.image.path}`}>
            <Picture value={s.image} alt={s.image.alt} className={moveOf(scene.id, k, scene.kind === 'group')} />
          </div>
        );
      })}
    </div>
  );
}

interface Props {
  singles: Slide[];
  groups: Slide[];
  current: Scene;
  leaving?: Scene;
  next: Scene;
}

export function HeroScenes({ singles, groups, current, leaving, next }: Props) {
  const layers: [Scene, Phase][] = [
    ...(leaving ? [[leaving, 'leaving'] as [Scene, Phase]] : []),
    [current, 'showing'],
    [next, 'waiting'],
  ];
  return (
    <>
      {layers.map(([scene, phase]) => {
        const pool = scene.kind === 'single' ? singles : groups;
        return pool.length ? <Layer key={scene.id} scene={scene} pool={pool} phase={phase} /> : null;
      })}
    </>
  );
}
